import { useState } from "react";
import { Button } from "reactstrap";
import { FaShareAlt, FaCheck } from "react-icons/fa";

interface ShareButtonProps {
  // Backend share URL (relative path is fine; we resolve against the current origin)
  // e.g. "/api/share/featured/CPT?window=20&direction=up"
  path: string;
  title?: string;
  // Visual variants
  label?: string;        // text next to the icon; omit for an icon-only button
  color?: string;        // reactstrap color (default "light")
  size?: string;         // "sm" | undefined
  className?: string;
  outline?: boolean;
}

// Shares a DejaChart signal/pattern. Uses the native share sheet when available
// (mobile), otherwise copies the link to the clipboard with brief feedback. The
// link points at the backend /api/share/... route, which unfurls a branded OG
// card and redirects humans to the app.
export default function ShareButton({
  path, title, label, color = "light", size, className, outline,
}: ShareButtonProps) {
  const [copied, setCopied] = useState(false);

  const handleShare = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const url = `${window.location.origin}${path}`;
    const data = { title: title ?? "DejaChart", url };
    if (typeof navigator.share === "function") {
      try {
        await navigator.share(data);
        return;
      } catch {
        // user cancelled or share failed — fall through to copy
      }
    }
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      // clipboard unavailable — last resort: open the share page in a new tab
      window.open(url, "_blank", "noopener");
    }
  };

  return (
    <Button
      color={color}
      size={size}
      outline={outline}
      className={className}
      onClick={handleShare}
      title="Share this signal"
      aria-label="Share"
    >
      {copied ? <FaCheck /> : <FaShareAlt />}
      {label ? <span className="ms-2">{copied ? "Copied!" : label}</span> : null}
    </Button>
  );
}
